import { Accordion, Group, Stack, Text } from '@mantine/core';
import { Entity } from '../../../enums/entity.enums';
import { AuditLogActions } from '../../../enums/retrieve-audit-logs.enum';
import { GetEnumKeyByEnumValue } from '../../../helpers/payment-link.helper';
import { BasicAuditLog } from '../../../type/retrieve-audit-logs.types';
import XSafeTextInfo from '../../x-components/x-safe-text-info.component';

interface Props {
  selectedLog: BasicAuditLog;
}

export function LogsDetailsSectionApprovedParentEntities(
  props: Readonly<Props>,
) {
  const { selectedLog } = props;
  return (
    <>
      <td
        style={{
          backgroundColor: '#F6F8FB',
          alignContent: 'flex-start',
        }}
      >
        <Text weight={600} align="right" size="sm">
          Approved under
        </Text>
      </td>
      <td>
        <Text size="sm">
          <Group>
            <XSafeTextInfo data="Partner:" />
            <XSafeTextInfo
              data={selectedLog.details[0].parentEntities?.parentPartner}
            />
          </Group>
          <Group>
            <XSafeTextInfo data="Reseller:" />
            <XSafeTextInfo
              data={selectedLog.details[0].parentEntities?.parentReseller}
            />
          </Group>
          <Group>
            <XSafeTextInfo data="Merchant:" />
            <XSafeTextInfo
              data={selectedLog.details[0].parentEntities?.parentMerchant}
            />
          </Group>
        </Text>
      </td>
    </>
  );
}

export function LogsDetailsSectionApprovedAsRow(props: Readonly<Props>) {
  const { selectedLog } = props;
  return (
    <>
      <td
        style={{
          backgroundColor: '#F6F8FB',
          alignContent: 'flex-start',
        }}
      >
        <Text weight={600} align="right" size="sm">
          Approved as
        </Text>
      </td>
      <td>
        <Text size="sm">
          <XSafeTextInfo
            data={GetEnumKeyByEnumValue(
              Entity,
              selectedLog.details[0].targetParentTypeId,
            )}
          />
        </Text>
      </td>
    </>
  );
}

export const getEntityType = (auditLog: BasicAuditLog) => {
  if (auditLog.details[0].targetParentTypeId === Entity.PARTNER) {
    return 'partner';
  } else if (auditLog.details[0].targetParentTypeId === Entity.MERCHANT) {
    return 'merchant';
  } else if (auditLog.details[0].targetParentTypeId === Entity.RESELLER) {
    return 'reseller';
  } else if (auditLog.details[0].targetParentTypeId === Entity.BRANCH) {
    return 'branch';
  } else if (auditLog.details[0].targetMerchantName) {
    return 'merchant';
  } else if (
    auditLog.logActionId === AuditLogActions.ENABLE_ACCOUNT ||
    auditLog.logActionId === AuditLogActions.DISABLE_ACCOUNT
  ) {
    return 'account';
  } else if (auditLog.logActionId === AuditLogActions.ASSIGN_MID) {
    return 'item';
  } else {
    return 'item';
  }
};

export function LogsDetailsSectionExportRow(props: Readonly<Props>) {
  const { selectedLog } = props;
  return (
    <>
      <td style={{ backgroundColor: '#F6F8FB' }}>
        <Text weight={600} align="right" size="sm">
          Target username
        </Text>
      </td>
      <td>
        <Text size="sm">{selectedLog.details[0].targetUsername}</Text>
      </td>
    </>
  );
}

const isMultipleTargetEntity = (targetEntities: Array<Object>): boolean => {
  const targetUsernames = targetEntities.map(
    (entity: any) => entity.targetUsername,
  );
  const groupedEntities = new Set(targetUsernames);
  return groupedEntities.size > 1;
};

export function LogsDetailsSectionDefaultRow(props: Readonly<Props>) {
  const { selectedLog } = props;
  return (
    <>
      <td
        style={{
          backgroundColor: '#F6F8FB',
          alignContent: 'flex-start',
        }}
      >
        {selectedLog.details[0].targetMerchantName &&
        isMultipleTargetEntity(selectedLog.details) ? (
          <>
            {selectedLog.details.length > 1 ? (
              <Text weight={600} align="right" size="sm">
                {/* This is done just so that the first letter is capitalized and the rest is not */}
                {getEntityType(selectedLog)?.charAt(0).toUpperCase() +
                  getEntityType(selectedLog).slice(1).toLowerCase()}{' '}
                names
              </Text>
            ) : (
              <Text weight={600} align="right" size="sm">
                {/* This is done just so that the first letter is capitalized and the rest is not */}
                {getEntityType(selectedLog).charAt(0).toUpperCase() +
                  getEntityType(selectedLog).slice(1).toLowerCase()}{' '}
                name
              </Text>
            )}
          </>
        ) : (
          <>
            {selectedLog.details.length > 1 &&
            isMultipleTargetEntity(selectedLog.details) ? (
              <Text weight={600} align="right" size="sm">
                Target usernames
              </Text>
            ) : (
              <Text weight={600} align="right" size="sm">
                Target username
              </Text>
            )}
          </>
        )}
      </td>
      <td>
        <Stack>
          {isMultipleTargetEntity(selectedLog.details) ? (
            selectedLog.details.map((detail, index) => {
              return (
                <Text key={index} size="sm">
                  {detail.targetMerchantName
                    ? detail.targetMerchantName
                    : detail.targetUsername}
                </Text>
              );
            })
          ) : (
            <Text size="sm">
              {selectedLog.details[0].targetMerchantName
                ? selectedLog.details[0].targetMerchantName
                : selectedLog.details[0].targetUsername}
            </Text>
          )}
        </Stack>
      </td>
    </>
  );
}

export function LogsDetailsSectionMidsUnassignRow(props: Readonly<Props>) {
  const { selectedLog } = props;
  return <></>;
}

export function LogsDetailsSectionCreateMidsRow(props: Readonly<Props>) {
  const { selectedLog } = props;
  return (
    <>
      <td style={{ backgroundColor: '#F6F8FB' }}>
        <Text weight={600} align="right" size="sm">
          Company Name
        </Text>
      </td>
      {
        <td>
          <Text size="sm">{selectedLog.details[0].targetMerchantName}</Text>
        </td>
      }
    </>
  );
}

export function LogsDetailsSectionUpdateRow(props: Readonly<Props>) {
  const { selectedLog } = props;
  return (
    <>
      <td style={{ backgroundColor: '#F6F8FB' }}>
        <Text weight={600} align="right" size="sm">
          Target
        </Text>
      </td>
      <td>
        <Text size="sm">
          {selectedLog.details[0].targetMerchantName ??
            selectedLog.details[0].targetUsername}
        </Text>
      </td>
    </>
  );
}

export function LogsDetailsSectionCreatePaymentRow(props: Readonly<Props>) {
  const { selectedLog } = props;

  return (
    <>
      <td
        style={{
          backgroundColor: '#F6F8FB',
          alignContent: 'flex-start',
        }}
      >
        <Text weight={600} align="right" size="sm">
          Product
        </Text>
      </td>
      <td
      style={{
        maxWidth: '100px', // Adjust as needed
      overflow: 'auto',
      }}>
        <Accordion
            >
          <Accordion.Item
            data-testid="accordion-item"
            iconPosition="right"
            label={selectedLog.details[0].new}
          >
            <Text size="sm">
              {selectedLog.details.map((log, index) => {
                if (index === 0) return;
                return (
                  <Group key={index}>
                    <XSafeTextInfo data={`${log.field}:`} />
                    <XSafeTextInfo data-testid="item-code" data={log?.new} />
                  </Group>
                );
              })}
            </Text>
          </Accordion.Item>
        </Accordion>
      </td>
    </>
  );
}
